import { Outlet, useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IEpisodeCategory } from '@common/interfaces/index';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization } from '@common/enums/index';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import type { IPersonalSettingsData } from './types';
import { PageTemplate } from '../../templates/PageTemplate';
import { PersonalSettingsProvider, usePersonalSettings } from './PersonalSettingsContext';
import { PersonalSettingsNavigation } from './PersonalSettingsNavigation';
import { PersonalSettingsProfile } from './PersonalSettingsProfile';

function PersonalSettingsContent() {
    const { employmentId } = useParams() as { employmentId: string };
    const abortableFetch = useAbortableFetch();
    const { personalSettingsData, isReadOnly } = usePersonalSettings();

    const isCurrentUser =
        useSelector((state: IReduxState) => state.authentication.id) === personalSettingsData?.practitioner.id;
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const [inactiveEpisodeCategories, setInactiveEpisodeCategories] = useState<IEpisodeCategory[]>([]);

    if (isManager) {
        assertHasAuthorization([ManagerAuthorization.employmentSettingsRead]);
    }

    if (!employmentId) {
        return null;
    }

    useEffect(() => {
        if (hasOneAuthorization([ManagerAuthorization.employmentSettingsRead])) {
            loadInactiveEpisodeCategories();
        }
    }, [employmentId]);

    if (!personalSettingsData) {
        return null;
    }

    return (
        <PageTemplate
            sideBarNavigation={renderNavigation(personalSettingsData)}
            sideBarProfile={renderProfile(personalSettingsData, 'large')}
            topBarNavigation={renderNavigation(personalSettingsData)}
            topBarProfile={renderProfile(personalSettingsData, 'small')}
            width="small"
        >
            <Outlet />
        </PageTemplate>
    );

    function renderProfile(personalSettings: IPersonalSettingsData, size: 'large' | 'small') {
        return (
            <PersonalSettingsProfile
                isActiveEmployment={personalSettings.isActiveEmployment}
                organization={personalSettings.organization}
                practitioner={personalSettings.practitioner}
                size={size}
            />
        );
    }

    function renderNavigation(personalSettings: IPersonalSettingsData) {
        return (
            <PersonalSettingsNavigation
                employmentId={employmentId}
                hasInactiveEpisodeCategories={inactiveEpisodeCategories.length > 0}
                inactiveEpisodeCategories={inactiveEpisodeCategories}
                isActiveEmployment={personalSettingsData?.isActiveEmployment}
                isCurrentUser={isCurrentUser}
                isReadOnly={isReadOnly}
                organizationId={personalSettings.organization.id}
                preferredServiceConsumerEpisodeCategories={personalSettings.preferredServiceConsumerEpisodeCategories}
                preferredServiceProviderEpisodeCategories={
                    personalSettings.preferredServiceProviderEpisodeCategories ?? []
                }
                serviceConsumerEpisodeCategories={personalSettings.serviceConsumers.map(
                    (serviceConsumer) => serviceConsumer.service.episodeCategory
                )}
                serviceConsumers={personalSettings.serviceConsumers}
                serviceEmploymentEpisodeCategories={personalSettings.serviceEmployments.map(
                    (serviceEmployment) => serviceEmployment.service.episodeCategory
                )}
                serviceProviderEpisodeCategories={personalSettings.serviceProviders.map(
                    (serviceProvider) => serviceProvider.service.episodeCategory
                )}
                serviceReaderEpisodeCategories={(personalSettings.serviceReaders || []).map(
                    (serviceReader) => serviceReader.service.episodeCategory
                )}
                serviceSupervisorEpisodeCategories={(personalSettings.serviceSupervisors || []).map(
                    (serviceSupervisor) => serviceSupervisor.service.episodeCategory
                )}
            />
        );
    }

    async function loadInactiveEpisodeCategories() {
        const url = `/api/employments/${employmentId}/inactiveEpisodeCategories`;
        const response = await abortableFetch.fetchJson(url, 'GET');
        setInactiveEpisodeCategories(response.json.items);
    }
}

export function PersonalSettings() {
    return (
        <PersonalSettingsProvider>
            <PersonalSettingsContent />
        </PersonalSettingsProvider>
    );
}
